/**
 * Design tokens for the login screen.
 *
 * The same palette the orders screen uses, restated here rather than imported
 * from containers/orders so the sign-in path carries no dependency on a
 * feature screen — the login screen has to render before anything else does.
 *
 * Nothing here is computed at runtime — import the token, never the literal.
 */
export const T = {
  // Surfaces
  canvas: '#F4F5F7',
  surface: '#FFFFFF',
  sunk: '#FAFAFB',
  hover: '#F5F6F8',
  border: '#E6E8EC',
  borderStrong: '#D8DCE2',
  hairline: '#F0F1F4',

  // Ink
  ink: '#15181D',
  inkSoft: '#6B7280',
  inkFaint: '#9AA1AC',
  inkGhost: '#C2C7CF',

  // Brand
  brand: '#F64829',
  brandInk: '#C3341A',
  brandTint: '#FFEBE6',
  selectedBg: '#FFF1ED',
  selectedBorder: '#F7C6B9',

  // Status
  paid: '#16A34A',
  paidTint: '#E9F7EF',
  pendingInk: '#B87409',
  pendingTint: '#FDF3E2',
  pendingBorder: '#E3C892',
  canceledTint: '#F1F2F4',

  // Destructive
  danger: '#DC2626',
  dangerTint: '#FDECEC',
  dangerBorder: '#F0B0AB',

  // Teal, borrowed from the orders summary band for a fourth avatar tint
  teal: '#04565E',
  tealTint: '#E2F0F1',

  // WhatsApp, for the support numbers under the sign-in card.
  //
  // Not WhatsApp's own #25D366: on this canvas that green measures 1.82:1,
  // nowhere near the 4.5:1 a body-size string needs, and at 11.5px it would
  // be close to unreadable. Darkened until it cleared — 4.84:1, and it still
  // reads as the right green from arm's length at a till.
  whatsapp: '#107C41',

  // Geometry
  radiusPanel: 10,
  radiusControl: 8,
  radiusDialog: 12,
  radiusPill: 999,
  controlHeight: 34,
  fieldHeight: 44,
  railWidth: 420,
  cardWidth: 380,

  // Elevation
  dialog: '0 12px 32px rgba(21,24,29,0.10)',
  toast: '0 8px 24px rgba(21,24,29,0.18)',
  backdrop: 'rgba(21,24,29,0.44)',
};

export const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/**
 * Eight avatar tints, so a rail of waiters is told apart at a glance rather
 * than read.
 *
 * Each waiter is given the least-used colour when their tile is created and
 * KEEPS it — the index is stored on the profile. Assigning by position would
 * mean everyone's colour shifted every time the tiles reordered, which is the
 * opposite of a recognisable face.
 *
 * Every ink clears 4.5:1 on its own background. The two carried over from the
 * orders screen (green, amber) were darkened to get there: they were drawn for
 * pills on white, not for initials on a tinted circle.
 */
export const AVATAR_TINTS = [
  { bg: '#FFEBE6', ink: '#C3341A' }, // red
  { bg: '#E6EFFC', ink: '#1D4ED8' }, // blue
  { bg: '#E9F7EF', ink: '#15803D' }, // green
  { bg: '#EFEAFB', ink: '#6D28D9' }, // violet
  { bg: '#FDF3E2', ink: '#8A5A06' }, // amber
  { bg: '#E2F0F1', ink: '#04565E' }, // teal
  { bg: '#FCE9F1', ink: '#BE185D' }, // pink
  { bg: '#EEF3DE', ink: '#3F6212' }, // olive
];

/** Hash of a username. Only for tiles pinned before colours were stored. */
export function avatarTint(seed) {
  const key = String(seed || '');
  let sum = 0;
  for (let i = 0; i < key.length; i += 1) sum += key.charCodeAt(i);
  return AVATAR_TINTS[sum % AVATAR_TINTS.length];
}

/**
 * The colour for one waiter. Reads the index stored on their profile and
 * falls back to the hash, so tiles saved by an older build still get a
 * sensible colour instead of a blank circle.
 */
export function tintFor(profile) {
  const index = profile?.tintIndex;
  if (typeof index === 'number' && AVATAR_TINTS[index]) return AVATAR_TINTS[index];
  return avatarTint(profile?.username || profile?.id);
}

/** "Amina Yusuf" -> "AY"; "amina" -> "AM". Matches ProductCard's rule. */
export function initialsOf(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
